"use client";
import { CheckboxCheckedContext } from "./checkbox-checked-context.js";
import { createElement, createHook, forwardRef } from "@ariakit/react-utils";
import { removeUndefinedValues } from "@ariakit/utils";
import { useContext } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/checkbox/checkbox-check.tsx
const TagName = "span";
const checkmark = /* @__PURE__ */ jsx("svg", {
	display: "block",
	fill: "none",
	stroke: "currentColor",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	strokeWidth: 1.5,
	viewBox: "0 0 16 16",
	height: "1em",
	width: "1em",
	children: /* @__PURE__ */ jsx("polyline", { points: "4,8 7,12 12,4" })
});
function getChildren(props) {
	if (props.checked) return props.children || checkmark;
	return null;
}
/**
* Returns props to create a `CheckboxCheck` component, that's usually rendered
* inside a `Checkbox` component.
* @see https://ariakit.com/components/checkbox
* @example
* ```jsx
* const props = useCheckboxCheck({ checked: true });
* <Role {...props} />
* ```
*/
const useCheckboxCheck = createHook(function useCheckboxCheck({ store, checked, ...props }) {
	const context = useContext(CheckboxCheckedContext);
	checked = checked ?? context;
	const children = getChildren({
		checked,
		children: props.children
	});
	props = {
		"aria-hidden": true,
		...props,
		children,
		style: {
			width: "1em",
			height: "1em",
			pointerEvents: "none",
			...props.style
		}
	};
	return removeUndefinedValues(props);
});
/**
* Renders a checkmark icon when the
* [`checked`](https://ariakit.com/reference/checkbox-check#checked) prop is
* `true`. The icon can be overridden by providing a different one as children.
*
* When rendered inside a [`Checkbox`](https://ariakit.com/reference/checkbox)
* component, the
* [`checked`](https://ariakit.com/reference/checkbox-check#checked) prop is
* automatically derived from the context.
* @see https://ariakit.com/components/checkbox
* @example
* ```jsx
* <CheckboxCheck checked />
* ```
*/
const CheckboxCheck = forwardRef(function CheckboxCheck(props) {
	return createElement(TagName, useCheckboxCheck(props));
});
//#endregion
export { CheckboxCheck, useCheckboxCheck };

//# sourceMappingURL=checkbox-check.js.map